import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import type { RootBlockModel } from '@blocksuite/affine-model';
import { CANVAS_OVERVIEW_TOGGLE_EVENT } from '@blocksuite/affine-shared/consts';
import {
  minimapDockGap,
  minimapDockRight,
  minimapDockTop,
} from '@blocksuite/affine-shared/styles';
import { WidgetComponent, WidgetViewExtension } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';
import { css, html, nothing, unsafeCSS } from 'lit';
import { state } from 'lit/decorators.js';
import { literal, unsafeStatic } from 'lit/static-html.js';

import type { EdgelessZoomToolbar } from './zoom-toolbar';
export {
  defineZoomControls,
  ZOOM_CONTROLS_TAG,
  ZoomControls,
  type ZoomControlsTarget,
} from './zoom-controls';
export { EdgelessZoomToolbar } from './zoom-toolbar';

export const AFFINE_EDGELESS_ZOOM_TOOLBAR_WIDGET =
  'affine-edgeless-zoom-toolbar-widget';

export class AffineEdgelessZoomToolbarWidget extends WidgetComponent<RootBlockModel> {
  static override styles = css`
    :host {
      position: absolute;
      top: ${unsafeCSS(minimapDockTop)};
      right: ${unsafeCSS(minimapDockRight)};
      z-index: var(--streamlain-z-index-popover);
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: ${unsafeCSS(minimapDockGap)};
      -webkit-user-select: none;
      user-select: none;
    }

    /* Product UI: only collapse to the toggle button on mobile. */
    @container viewport (width <= 640px) {
      edgeless-zoom-toolbar {
        display: none;
      }
    }

    @container viewport (width > 640px) {
      zoom-bar-toggle-button {
        display: none;
      }
    }
  `;

  get edgeless() {
    return this.block;
  }

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  override connectedCallback() {
    super.connectedCallback();

    this.disposables.addFromEvent(this.host, CANVAS_OVERVIEW_TOGGLE_EVENT, () =>
      this.toggleOverview()
    );
    // What lies over the view's end moves a dock at that end out of its way.
    this.dataset.viewEndDock = '';
    // The minimap dock covers the view's top right corner: fits keep content clear of it.
    this.disposables.add(this.gfx.viewport.registerOverlay(() => this));
    this.disposables.add(
      effect(() => {
        const currentTool = this.gfx.tool.currentToolName$.value;

        if (currentTool !== 'frameNavigator') {
          this._hide = false;
        }
        this.requestUpdate();
      })
    );
  }

  /** Open the canvas overview, or close it if it is open. */
  toggleOverview() {
    this.renderRoot
      .querySelector<EdgelessZoomToolbar>('edgeless-zoom-toolbar')
      ?.toggleOverview();
  }

  override firstUpdated() {
    const { disposables, std } = this;
    const slots = std.get(EdgelessLegacySlotIdentifier);

    disposables.add(
      slots.navigatorSettingUpdated.subscribe(({ hideToolbar }) => {
        if (hideToolbar !== undefined) {
          this._hide = hideToolbar;
        }
      })
    );
  }

  override render() {
    if (this._hide || !this.edgeless) {
      return nothing;
    }

    return html`
      <edgeless-zoom-toolbar .std=${this.std}></edgeless-zoom-toolbar>
      <zoom-bar-toggle-button .std=${this.std}></zoom-bar-toggle-button>
    `;
  }

  @state()
  private accessor _hide = false;
}

export const edgelessZoomToolbarWidget = WidgetViewExtension(
  'affine:page',
  AFFINE_EDGELESS_ZOOM_TOOLBAR_WIDGET,
  literal`${unsafeStatic(AFFINE_EDGELESS_ZOOM_TOOLBAR_WIDGET)}`
);
